import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, Loader, Code, Layers } from 'lucide-react';
import toast from 'react-hot-toast';

export default function FranceTravailTest() {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [macroCompetences, setMacroCompetences] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingMacro, setLoadingMacro] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Test de connexion
  const handleTestConnection = async () => {
    setTesting(true);
    try {
      console.log('🧪 Test de connexion à France Travail API...');

      const response = await fetch(`${import.meta.env.VITE_API_URL}/france-travail/test-connection`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      console.log('📊 Résultat:', data);

      if (data.success) {
        setConnectionStatus('success');
        toast.success('Connexion réussie à France Travail API !');
      } else {
        setConnectionStatus('error');
        toast.error(data.message || 'Échec de connexion');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      setConnectionStatus('error');
      toast.error('Erreur lors du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  // Recherche de compétences
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Veuillez entrer un terme de recherche');
      return;
    }

    setSearching(true);
    setResults([]);
    setSelectedItem(null);

    try {
      console.log(`🔍 Recherche compétences: "${searchQuery}"`);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/france-travail/competences?query=${encodeURIComponent(searchQuery)}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      console.log('📊 Résultats:', data);

      if (data.success) {
        setResults(data.data);
        toast.success(`${data.count} résultat(s) trouvé(s)`);
      } else {
        toast.error(data.message || 'Erreur lors de la recherche');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };

  // Charger les macro-compétences
  const handleLoadMacroCompetences = async () => {
    setLoadingMacro(true);
    try {
      console.log('🔍 Chargement des macro-compétences...');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/france-travail/macro-competences`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      console.log('📊 Macro-compétences:', data);

      if (data.success) {
        setMacroCompetences(data.data);
        toast.success(`${data.count} macro-compétence(s) chargée(s)`);
      } else {
        toast.error(data.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors du chargement des macro-compétences');
    } finally {
      setLoadingMacro(false);
    }
  };

  // Voir les détails d'une compétence
  const handleViewDetails = async (item) => {
    try {
      console.log(`🔍 Détails de:`, item);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/france-travail/competences/${item.code}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      console.log('📊 Détails:', data);

      if (data.success) {
        setSelectedItem(data.data);
      } else {
        toast.error(data.message || 'Erreur lors de la récupération des détails');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors de la récupération des détails');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Test API France Travail - ROME 4.0
          </h1>
          <p className="text-gray-600">
            Interface de test pour l'API ROME 4.0 - Compétences v1
          </p>
        </div>

        {/* Test de connexion */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            1. Test de connexion OAuth2
          </h2>
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="btn btn-primary flex items-center gap-2"
          >
            {testing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Test en cours...
              </>
            ) : (
              <>
                {connectionStatus === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : connectionStatus === 'error' ? (
                  <XCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <Code className="w-5 h-5" />
                )}
                Tester la connexion
              </>
            )}
          </button>

          {connectionStatus && (
            <div className={`mt-4 p-4 rounded-lg ${
              connectionStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {connectionStatus === 'success' ? (
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  ✅ Connexion réussie ! Token OAuth2 obtenu.
                </p>
              ) : (
                <p className="flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  ❌ Échec de connexion. Vérifiez vos credentials dans le .env
                </p>
              )}
            </div>
          )}
        </div>

        {/* Recherche de compétences */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            2. Recherche de compétences
          </h2>

          {/* Barre de recherche */}
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Rechercher une compétence..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="btn btn-primary flex items-center gap-2"
            >
              {searching ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              Rechercher
            </button>
          </div>

          {/* Résultats */}
          {results.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {results.length} résultat(s)
              </h3>
              <div className="grid gap-3">
                {results.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleViewDetails(item)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Code className="w-4 h-4 text-blue-600" />
                          <span className="font-mono text-sm text-gray-500">
                            {item.code}
                          </span>
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          {item.libelle}
                        </h4>
                        {item.definition && (
                          <p className="text-sm text-gray-600 mt-1">
                            {item.definition}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Macro-compétences */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            3. Macro-compétences
          </h2>
          <button
            onClick={handleLoadMacroCompetences}
            disabled={loadingMacro}
            className="btn btn-secondary flex items-center gap-2"
          >
            {loadingMacro ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Chargement...
              </>
            ) : (
              <>
                <Layers className="w-5 h-5" />
                Charger les macro-compétences
              </>
            )}
          </button>

          {macroCompetences.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {macroCompetences.length} macro-compétence(s)
              </h3>
              <div className="grid gap-3">
                {macroCompetences.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Layers className="w-4 h-4 text-purple-600" />
                      <span className="font-mono text-sm text-gray-500">
                        {item.code}
                      </span>
                    </div>
                    <h4 className="font-semibold text-gray-900">
                      {item.libelle}
                    </h4>
                    {item.definition && (
                      <p className="text-sm text-gray-600 mt-1">
                        {item.definition}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Détails */}
        {selectedItem && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              4. Détails de la compétence
            </h2>
            <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-sm">
              {JSON.stringify(selectedItem, null, 2)}
            </pre>
          </div>
        )}

        {/* Documentation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            📚 Configuration
          </h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p><strong>1.</strong> Créez une application sur <a href="https://francetravail.io/" target="_blank" rel="noopener noreferrer" className="underline">francetravail.io</a></p>
            <p><strong>2.</strong> Récupérez votre <code className="bg-blue-100 px-2 py-1 rounded">Identifiant client</code> et <code className="bg-blue-100 px-2 py-1 rounded">Clé secrète</code></p>
            <p><strong>3.</strong> Configurez dans <code className="bg-blue-100 px-2 py-1 rounded">backend/.env</code> :</p>
            <pre className="bg-blue-100 p-3 rounded mt-2 overflow-auto">
{`FT_CLIENT_ID=votre_client_id
FT_CLIENT_SECRET=votre_client_secret
FT_SCOPE=api_rome-competencesv1 nomenclatureRome`}
            </pre>
            <p><strong>4.</strong> URL d'accès à configurer sur francetravail.io : <code className="bg-blue-100 px-2 py-1 rounded">http://localhost:3000</code></p>
            <p className="mt-4"><strong>API utilisée :</strong> ROME 4.0 - Compétences v1</p>
            <p><strong>Endpoints disponibles :</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li><code className="bg-blue-100 px-2 py-1 rounded">/competences/competence</code> - Liste des compétences</li>
              <li><code className="bg-blue-100 px-2 py-1 rounded">/competences/competence-detaillee</code> - Détails d'une compétence</li>
              <li><code className="bg-blue-100 px-2 py-1 rounded">/competences/macro-competence</code> - Macro-compétences</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
